import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import Container from '../../components/shared/Container';
import Button from '../../components/shared/Button';
import { Link } from 'react-router-dom';
import { useHeader } from '../../context/HeaderContext';
import SEO from '../../components/shared/SEO';
import StructuredData from '../../components/shared/StructuredData';
import { generateBreadcrumbSchema } from '../../utils/schema';

// Animation variants
const fadeIn = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { duration: 0.8 } }
};

const sectionVariants = {
  hidden: { opacity: 0 },
  visible: { 
    opacity: 1,
    transition: {
      staggerChildren: 0.3
    }
  }
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { 
    opacity: 1, 
    y: 0,
    transition: {
      duration: 0.8,
      ease: "easeOut"
    }
  }
};

export default function AboutPage() {
  const { setUseBlurredBackground } = useHeader();

  // Enable blurred background for header on about page
  useEffect(() => {
    setUseBlurredBackground(true);
    
    // Clean up when component unmounts
    return () => {
      setUseBlurredBackground(false);
    };
  }, [setUseBlurredBackground]);

  const images = [
    '/images/about/about1.jpg',
    '/images/about/about2.jpg',
    '/images/about/about3.jpg',
    '/images/about/about4.jpg',
    '/images/about/about5.jpg',
    '/images/about/about6.jpg',
    '/images/about/about7.jpg',
    '/images/about/about8.jpg',
  ];

  return (
    <div className="bg-brand-cream min-h-screen">
      <SEO 
        title="About Us | The Original Pancake House - Our Story Since 1953" 
        description="Discover the story of The Original Pancake House, founded in Portland, Oregon in 1953. Learn about our commitment to fresh ingredients, handcrafted recipes, and warm hospitality that has made us a beloved breakfast destination for over 70 years."
        canonicalUrl="/about"
        openGraph={{
          image: "/oph-social-share4.jpg"
        }}
      />
      
      {/* Structured Data for SEO */}
      <StructuredData data={generateBreadcrumbSchema([
        { name: 'Home', url: '/' },
        { name: 'About', url: '/about' }
      ])} />
      
      {/* Hero Section */}
      <motion.div 
        className="relative h-[400px] bg-brand-navy"
        initial="hidden"
        animate="visible"
        variants={fadeIn}
        style={{
          backgroundImage: `url(${images[0]})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        {/* Gradient Overlay - Darker on mobile for better text readability */}
        <div className="absolute inset-0 bg-gradient-to-t from-brand-navy via-brand-navy/60 to-brand-navy/30 lg:via-transparent lg:to-transparent"></div>
        
        <Container className="relative h-full">
          <div className="absolute bottom-8 left-0 right-0 w-full px-6 md:px-8 lg:px-12">
            <div className="flex flex-col md:flex-row md:justify-between md:items-end">
              {/* Left side - Title and breadcrumb */}
              <div className="mb-4 md:mb-0">
                <div className="mb-4">
                  <div className="flex items-center text-sm font-medium">
                    <Link 
                      to="/"
                      className="text-white hover:text-brand-cream transition-colors"
                    >
                      Home
                    </Link>
                    
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 mx-2 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                    
                    <span className="text-white text-opacity-90">
                      About Us
                    </span>
                  </div>
                </div>
                <h1 className="text-5xl md:text-6xl font-heading text-white drop-shadow-lg">About OPH</h1>
              </div>
              
              {/* Right side - Description */}
              <div className="md:max-w-xl">
                <p className="text-white text-opacity-90 drop-shadow-md">
                  Since 1953, we've been dedicated to serving the finest breakfast using only the highest quality ingredients and time-honored recipes.
                </p>
              </div>
            </div>
          </div>
        </Container>
      </motion.div>

      {/* History Section */}
      <section className="py-16 bg-white">
        <Container>
          <motion.div
            variants={sectionVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="max-w-6xl mx-auto"
          >
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <motion.div variants={itemVariants}>
                <h2 className="text-4xl font-heading text-brand-navy mb-6">
                  A TRADITION OF EXCELLENCE
                </h2>
                <div className="space-y-4 text-gray-700">
                  <p>
                    The Original Pancake House was founded in Portland, Oregon in 1953 by Les Highet and Erma Hueneke. Drawing upon their many years of experience in the culinary field, and their extensive working knowledge of authentic national and ethnic pancake recipes they were able to offer, without compromise, this unique and original menu which has gained national acclaim.
                  </p>
                  <p>
                    The Original Pancake House is a second and third generation family business, which takes great pride in maintaining the high standards that make our food so outstanding. There are now over one hundred franchises from coast to coast, which have won local and national acclaim.
                  </p>
                  <p>
                    Our recipes demand only the very finest of ingredients, such as 93 score butter, pure 36% whipping cream, fresh grade AA eggs, hard wheat unbleached flour, and our own recipe sourdough starter. Our batters and sauces are made fresh in each restaurant's kitchen.
                  </p>
                </div>
              </motion.div>

              <motion.div variants={itemVariants} className="grid grid-cols-2 gap-4">
                <img 
                  src={images[1]} 
                  alt="Original Pancake House interior" 
                  className="w-full h-64 object-cover rounded-lg shadow-md"
                  loading="lazy"
                />
                <img 
                  src={images[2]} 
                  alt="Original Pancake House food" 
                  className="w-full h-64 object-cover rounded-lg shadow-md mt-8"
                  loading="lazy"
                />
              </motion.div>
            </div>
          </motion.div>
        </Container>
      </section>

      {/* Our Process Section */}
      <section className="py-16 bg-brand-cream">
        <Container>
          <motion.div
            variants={sectionVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="max-w-6xl mx-auto"
          >
            <motion.h2 variants={itemVariants} className="text-4xl font-heading text-brand-navy text-center mb-12">
              TASTE THE DIFFERENCE
            </motion.h2>

            <div className="grid md:grid-cols-3 gap-8 mb-12">
              {/* Quality Ingredients */}
              <motion.div variants={itemVariants} className="bg-white p-6 rounded-lg shadow-md">
                <div className="w-16 h-16 rounded-full bg-brand-navy flex items-center justify-center mb-4 mx-auto">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-brand-navy mb-3 text-center">QUALITY INGREDIENTS</h3>
                <p className="text-gray-700 text-center">
                  We use only the highest quality ingredients: 93 score AA grade clarified butter, fresh grade AA eggs, and specialty batters made from scratch daily.
                </p>
              </motion.div>

              {/* Fresh Preparation */}
              <motion.div variants={itemVariants} className="bg-white p-6 rounded-lg shadow-md">
                <div className="w-16 h-16 rounded-full bg-brand-navy flex items-center justify-center mb-4 mx-auto">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-brand-navy mb-3 text-center">FRESH DAILY</h3>
                <p className="text-gray-700 text-center">
                  Our corned beef hash is slow-roasted for 8 hours in-house. Orange juice is freshly squeezed each morning from real oranges - no water, no sugar, no preservatives.
                </p>
              </motion.div>

              {/* World-Class Hospitality */}
              <motion.div variants={itemVariants} className="bg-white p-6 rounded-lg shadow-md">
                <div className="w-16 h-16 rounded-full bg-brand-navy flex items-center justify-center mb-4 mx-auto">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-brand-navy mb-3 text-center">HOSPITALITY</h3>
                <p className="text-gray-700 text-center">
                  We're committed to exceeding expectations at every turn. Your needs should be filled before they occur to you, and always with a smile.
                </p>
              </motion.div>
            </div>

            {/* Full Process Description */}
            <motion.div variants={itemVariants} className="bg-white p-8 rounded-lg shadow-md">
              <p className="text-gray-700 mb-4">
                At OPH, it begins with the food and a total commitment to a better process. This means using whole ingredients, preparing fresh food daily and not accepting shortcuts. As the name indicates, we offer a wide variety of pancakes, crepes and waffles, requiring a number of unique, specialty batters. To achieve the ideal taste texture and freshness, each batter is made from scratch every day using only the highest quality ingredients.
              </p>
              <p className="text-gray-700 mb-4">
                We take every aspect of food preparation into consideration. Our freshly cracked eggs, home prepared potatoes and hand rolled crepes are cooked to order in 93 score, AA grade clarified butter. For the butter novice, this is the highest quality product available and it is all we use.
              </p>
              <p className="text-gray-700">
                A great atmosphere isn't just inviting décor and creative theming. Producing a great atmosphere requires a constant attention to detail. Your experience should be clean and inviting from the moment you walk through our doors. Come enjoy our old-world charm with a modern twist while enjoying the tastiest breakfast on the market.
              </p>
            </motion.div>
          </motion.div>
        </Container>
      </section>

      {/* Image Gallery Section */}
      <section className="py-16 bg-white">
        <Container>
          <motion.div
            variants={sectionVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="max-w-6xl mx-auto"
          >
            <motion.h2 variants={itemVariants} className="text-4xl font-heading text-brand-navy text-center mb-12">
              EXPERIENCE OPH
            </motion.h2>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {images.map((image, index) => (
                <motion.div 
                  key={index}
                  variants={itemVariants}
                  className="aspect-square overflow-hidden rounded-lg shadow-md hover:shadow-xl transition-shadow"
                >
                  <img 
                    src={image} 
                    alt={`Original Pancake House ${index + 1}`}
                    className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                    loading="lazy"
                  />
                </motion.div>
              ))}
            </div>
          </motion.div>
        </Container>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-brand-navy text-white">
        <Container>
          <motion.div
            variants={sectionVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="max-w-4xl mx-auto text-center"
          >
            <motion.h2 variants={itemVariants} className="text-4xl font-heading mb-6">
              READY TO EXPERIENCE THE DIFFERENCE?
            </motion.h2>
            <motion.p variants={itemVariants} className="text-xl text-white text-opacity-90 mb-8">
              Join us for breakfast or brunch and taste the quality that has made us a tradition since 1953.
            </motion.p>
            <motion.div variants={itemVariants} className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                as={Link}
                to="/menu"
                variant="solid" 
                color="cream"
                className="text-lg px-8 py-3"
              >
                View Our Menu
              </Button>
              <Button 
                as={Link}
                to="/locations"
                variant="outline" 
                color="cream"
                className="text-lg px-8 py-3"
              >
                Find a Location
              </Button>
              <Button 
                as={Link}
                to="/loyalty"
                variant="outline" 
                color="cream"
                className="text-lg px-8 py-3"
              >
                Join Our Loyalty Program
              </Button>
            </motion.div>
          </motion.div>
        </Container>
      </section>

      {/* Additional Links Section */}
      <section className="py-12 bg-brand-cream">
        <Container>
          <div className="max-w-4xl mx-auto">
            <div className="grid md:grid-cols-3 gap-6">
              <Link 
                to="/gift-cards"
                className="bg-white p-6 rounded-lg shadow-md hover:shadow-xl transition-shadow text-center group"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 mx-auto mb-4 text-brand-navy group-hover:text-brand-orange transition-colors" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v13m0-13V6a2 2 0 112 2h-2zm0 0V5.5A2.5 2.5 0 109.5 8H12zm-7 4h14M5 12a2 2 0 110-4h14a2 2 0 110 4M5 12v7a2 2 0 002 2h10a2 2 0 002-2v-7" />
                </svg>
                <h3 className="text-xl font-bold text-brand-navy mb-2">Gift Cards</h3>
                <p className="text-gray-600">Share the joy of breakfast</p>
              </Link>

              <a 
                href="https://bte-management-company.r365hire.com/account"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-white p-6 rounded-lg shadow-md hover:shadow-xl transition-shadow text-center group"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 mx-auto mb-4 text-brand-navy group-hover:text-brand-orange transition-colors" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                <h3 className="text-xl font-bold text-brand-navy mb-2">Careers</h3>
                <p className="text-gray-600">Join our team</p>
              </a>

              <a 
                href="/oph-nutritional-info.pdf"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-white p-6 rounded-lg shadow-md hover:shadow-xl transition-shadow text-center group"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 mx-auto mb-4 text-brand-navy group-hover:text-brand-orange transition-colors" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <h3 className="text-xl font-bold text-brand-navy mb-2">Nutrition Info</h3>
                <p className="text-gray-600">View nutritional details</p>
              </a>
            </div>
          </div>
        </Container>
      </section>
    </div>
  );
}

